import React, { FC, useMemo } from 'react'
import { ExpandLess } from '@mui/icons-material'
import {
  Box,
  Card,
  CardProps,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AmericanStatesSelector } from '../../store/AmericanStates'
import { CountySelector } from '../../store/County'
import {
  WaterSystemsMetricsActions,
  WaterSystemsMetricsSelector,
} from '../../store/publicWaterSystem'
import { WaterSystemsSelector, WaterSystemType } from '../../store/waterSystems'
import { ZipCodeSelector } from '../../store/ZipCode'
import { generateLocationTitle, getViolationColor } from '../../utils/waterSystemHelper'
import { RoundLinkIcon, UsersGroupIcon, ViolationIcon } from '../icons'
import { WellWaterSystemsMetricsActions, WellWaterSystemsMetricsSelector, WaterSystemGeoBlocksSelector } from '../../store/wellWaterSystem'
import { AddressSelector } from '../../store/Address'

const InfoCardsData = [
  { icon: <RoundLinkIcon />, label: 'Service connections', valueKey: 'totalConnections' },
  { icon: <UsersGroupIcon />, label: 'Total Population', valueKey: 'totalPopulation' },
]

const getMetricDisplayData = (waterSystemType: WaterSystemType) => {
  if (waterSystemType === WaterSystemType.WELL) {
    return {
      mainMetric: {
        key: 'totalHousingUnits',
        label: 'Total Housing Units',
        icon: <UsersGroupIcon />,
      }
    }
  }
  return {
    mainMetric: {
      key: 'totalViolations',
      label: 'Total Violations',
      icon: <ViolationIcon />,
    }
  }
}

interface UtilitiesMetrics2Props {
  CardProps?: CardProps
}

export const UtilitiesMetrics2:FC<UtilitiesMetrics2Props> = ({CardProps = {}}) => {
  const {sx: cardSx, ...CardPropsRest} = CardProps
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const waterSystemsMetrics: any = useSelector(WaterSystemsMetricsSelector.selectWaterSystemsData)
  const wellWaterSystemsMetrics: any = useSelector(WellWaterSystemsMetricsSelector.selectWellWaterSystemsData)
  const waterSystemType = useSelector(WaterSystemsSelector.getSelectedWaterSystemType)
  const waterSystems = useSelector(WaterSystemsSelector.makeWaterSystems)
  const selectedSystemId: string = useSelector(WaterSystemsSelector.getSelectedWaterSystem)
  const loading = useSelector(WaterSystemsMetricsSelector.loading)
  const selectedState = useSelector(AmericanStatesSelector.getSelectedState)
  const selectedCounty = useSelector(CountySelector.selectCounty)
  const selectedZipCode = useSelector(ZipCodeSelector.selectZipCode)
  const selectedGeoBlock = useSelector(WaterSystemGeoBlocksSelector.selectGeoBlock)
  const selectedAddress = useSelector(AddressSelector.selectAddress)
  const [previousZipCode, setPreviousZipCode] = useState<string | null | undefined>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  //const title = generateLocationTitle(selectedState.state, selectedCounty, selectedZipCode, selectedGeoBlock, selectedAddress)

  const getMetricsData = () => {
    if (waterSystemType === WaterSystemType.WELL) {
      return wellWaterSystemsMetrics?.[0] || {}
    }

    return waterSystemsMetrics?.[0] || {}
  }
  const getTitleValue = () => {

    if (selectedSystemMetrics && selectedSystemMetrics.PWS_NAME) {
      return selectedSystemMetrics.PWS_NAME
    }
    return generateLocationTitle(selectedState.state, selectedCounty, selectedZipCode, selectedGeoBlock, selectedAddress)
  }
  const getMetricValue = (metricKey: string) => {

    if (selectedSystemMetrics) {
      return selectedSystemMetrics[metricKey]?.toLocaleString() || '--'
    } else {
      const metricsData = getMetricsData()
      return metricsData[metricKey]?.toLocaleString() || '--'
    }
  }

  const selectedSystemMetrics = useMemo(() => 
    selectedSystemId
      ? waterSystems.find((system: any) => system.PWSID === selectedSystemId)
      : null,
    [selectedSystemId, waterSystems]
  )

  const metricDisplay = getMetricDisplayData(waterSystemType)

  const mainMetricValue = getMetricValue(metricDisplay.mainMetric.key)
  const color = waterSystemType === WaterSystemType.WELL 
    ? theme.palette.primary.main 
    : getViolationColor(Number(mainMetricValue.replace(/,/g, '')), theme)

  useEffect(() => {
    if (!selectedState.state) {
      if (waterSystemType === WaterSystemType.WELL) {
        dispatch(WellWaterSystemsMetricsActions.fetchWellWaterSystemMetricsAsync({}) as any)
      } else {
        dispatch(WaterSystemsMetricsActions.fetchWaterSystemMetricsAsync({}) as any)
      }
    }
  }, [waterSystemType, selectedState.state, dispatch])

  useEffect(() => {
    if (waterSystems && waterSystems.length > 0) {
      const zipcodes = waterSystems[0].ZIP_CODE

      if (zipcodes !== previousZipCode) {
        const options = {
          params: {
            zip_codes: zipcodes,
          },
        }

        if (waterSystemType === WaterSystemType.WELL) {
          dispatch(WellWaterSystemsMetricsActions.fetchWellWaterSystemMetricsAsync({ options }) as any)
        } else {
          dispatch(WaterSystemsMetricsActions.fetchWaterSystemMetricsAsync({ options }) as any)
        }
        setPreviousZipCode(zipcodes)
      }
    }
  }, [dispatch, waterSystems, previousZipCode, waterSystemType])

  return (
    <Card
      {...CardPropsRest}
      sx={{
        boxShadow: 'none',
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.9)',
        border: `1px solid ${theme.palette.grey[200]}`,
        width: isMobile ? '209px' : '250px',
        overflow: 'hidden',
        p: isMobile ? 1.5 : 2,
        ...cardSx
      }}
    >
      <Stack gap={{ xs: 1, sm: 2 }}>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Typography
            fontSize="1rem"
            color="primary.dark"
            fontWeight={theme.typography.fontWeightBold}
          >
            {getTitleValue()}
          </Typography>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <ExpandLess
              sx={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
              }}
            />
          </IconButton>
        </Stack>
        {!isMobile && (
          <Box
            sx={{
              pt: 1.5,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack
              direction="column"
              gap={1}
              sx={{
                borderLeft: `2px solid ${color}`,
                pb: 1.5,
                pl: 2,
              }}
            >
              {React.cloneElement(metricDisplay.mainMetric.icon, { fillColor: color, sx: { color: 'transparent' } })}
              <Typography
                fontSize="1.125rem"
                color={color}
                fontWeight={theme.typography.fontWeightBold}
              >
                {loading ? (
                  <Skeleton variant="text" sx={{ fontSize: '0.75rem', height: 27, width: '40%' }} />
                ) : (
                  mainMetricValue
                )}
              </Typography>
              <Typography fontSize="1rem" color="primary.dark" textTransform={'lowercase'}>
                {metricDisplay.mainMetric.label}
              </Typography>
            </Stack>
          </Box>
        )}

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box mt={{ xs: 0, sm: 1 }}>
            {waterSystemType !== WaterSystemType.WELL && InfoCardsData.map((metric, index) => (
              <Stack
                key={index}
                direction={{ xs: 'row', sm: 'column' }}
                gap={1}
                sx={{
                  py: 1.5,
                  borderTop: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
                }}
              >
                {metric.icon}
                <Typography
                  fontSize="1.125rem"
                  color="primary.dark"
                  fontWeight={theme.typography.fontWeightBold}
                >
                  {loading ? (
                    <Skeleton variant="text" sx={{ fontSize: '1rem', height: 27, width: '50%' }} />
                  ) : (
                    getMetricValue(metric.valueKey)
                  )}
                </Typography>
                {!isMobile && (
                  <Typography fontSize="1rem" color="primary.dark" textTransform={'lowercase'}>
                    {metric.label}
                  </Typography>
                )}
              </Stack>
            ))}
            
            {isMobile && (
              <Box
                sx={{
                  pt: 1.5,
                }}
              >
                <Stack
                  direction={{ xs: 'row', sm: 'column' }}
                  gap={1}
                  sx={{
                    borderLeft: `2px solid ${color}`,
                    pb: 1.5,
                    pl: 2,
                  }}
                >
                  {React.cloneElement(metricDisplay.mainMetric.icon, { fillColor: color, sx: { color: 'transparent' } })}
                  <Typography
                    fontSize="1.125rem"
                    color={color}
                    fontWeight={theme.typography.fontWeightBold}
                  >
                    {loading ? (
                      <Skeleton
                        variant="text"
                        sx={{ fontSize: '0.75rem', height: 27, width: '40%' }}
                      />
                    ) : (
                      mainMetricValue
                    )}
                  </Typography>
                </Stack>
              </Box>
            )}
          </Box>
        </Collapse>
      </Stack>
    </Card>
  )
}
